/**
 * Unified Configuration System for ProDebtCo Lead Tracker
 * Consolidates all configuration sources into a single, environment-aware system
 * 
 * Replaces:
 * - src/config/index.ts (generic app config)
 * - src/config/production.ts (hardcoded Supabase credentials)
 * - src/supabase/config.ts (complex config selection)
 * - lead-tracker.js process.env expectations
 */

// Unified configuration interface
export interface UnifiedConfig {
  // Environment
  environment: 'development' | 'production'
  debug: boolean
  version: string
  
  // Supabase Configuration
  supabase: {
    url: string
    anonKey: string
    serviceRoleKey?: string
  }
  
  // API Configuration
  api: {
    baseUrl?: string
    timeout: number
    retryAttempts: number
    retryDelay: number
  }
  
  // Edge Functions
  edgeFunctions: {
    baseUrl: string
    endpoints: {
      createSession: string
      processFormInteraction: string
      simpleLeadProcessor: string
      phoneLookup: string
    }
  }

  // dcapi endpoint for the CD additional-information capture flow (scenarios 5 & 7).
  // Owned by the dcapi team. Frontend posts the captured info here on submit;
  // response uses the same shape as credit-decision and includes a redirect URL
  // on accept.
  additionalInformationUrl: string

  // External API (for form pre-fill)
  externalApi: {
    baseUrl: string
    endpoints: {
      leadByRefId: string
    }
    headers: {
      'x-api-auth': string
    }
    timeout: number
  }

  // Session Management
  session: {
    timeout: number              // 30 minutes
    renewThreshold: number       // 5 minutes before expiry
    maxRetries: number
    storageKey: string
  }
  
  // Lead Detection
  leadDetection: {
    partialLeadThreshold: number        // 30% completion
    completeLeadThreshold: number       // 80% completion
    minFieldsForPartialLead: number     // 2 fields minimum
    contactFieldWeight: number          // 40% weight for contact info
    importantFields: string[]           // Key fields for lead processing
  }
  
  // Event Processing
  eventProcessing: {
    batchSize: number                   // 50 events per batch
    flushInterval: number               // 5 seconds
    maxQueueSize: number                // 1000 max events
    enableRealtime: boolean
  }
  
  // Rate Limiting
  rateLimiting: {
    sessionCreation: number             // 10 per minute
    formInteraction: number             // 120 per minute
    leadProcessing: number              // 5 per minute
    burstLimit: {
      sessionCreation: number           // 2 per second
      formInteraction: number           // 10 per second
      leadProcessing: number            // 1 per second
    }
  }
}

// Get package version
const getVersion = (): string => {
  try {
    return require('../../package.json').version || '1.0.0'
  } catch {
    return '1.0.0'
  }
}

// Environment detection
const getEnvironment = (): 'development' | 'production' => {
  // In webpack, process.env.NODE_ENV is replaced via DefinePlugin
  return (process.env.NODE_ENV === 'production') ? 'production' : 'development'
}

// Create unified configuration
const createUnifiedConfig = (): UnifiedConfig => {
  const environment = getEnvironment()
  const isDevelopment = environment === 'development'
  
  // Get environment variables (replaced by webpack DefinePlugin in production)
  const supabaseUrl = process.env.SUPABASE_URL || 'https://dwyvdtpzkkwnvowphvju.supabase.co'
  const supabaseAnonKey = process.env.SUPABASE_ANON_KEY || 'sb_publishable_t_xEURdLDU_4Tm-BiCTu7Q_xrG---4Z'
  
  return {
    // Environment
    environment,
    debug: isDevelopment,
    version: getVersion(),
    
    // Supabase Configuration  
    supabase: {
      url: supabaseUrl,
      anonKey: supabaseAnonKey,
      serviceRoleKey: process.env.SUPABASE_SERVICE_ROLE_KEY // Server-side only
    },
    
    // API Configuration
    api: {
      baseUrl: process.env.API_ENDPOINT || (isDevelopment ? 'http://localhost:3000/api' : 'https://api.yourdomain.com'),
      timeout: 30000,       // 30 seconds
      retryAttempts: 3,
      retryDelay: 1000      // 1 second
    },
    
    // Edge Functions
    edgeFunctions: {
      baseUrl: `${supabaseUrl}/functions/v1`,
      endpoints: {
        createSession: `${supabaseUrl}/functions/v1/create-session`,
        processFormInteraction: `${supabaseUrl}/functions/v1/process-form-interaction`,
        simpleLeadProcessor: `${supabaseUrl}/functions/v1/simple-lead-processor`,
        phoneLookup: `${supabaseUrl}/functions/v1/phone-lookup`
      }
    },

    // Additional-information capture endpoint (CD scenarios 5 & 7).
    // Per the dcapi integration spec (docs/additional-information-api.md) this
    // is the dcapi host — NOT the macenapi/n8n host used for credit-decision.
    additionalInformationUrl:
      process.env.ADDITIONAL_INFORMATION_URL ||
      'https://dcapi.maceinnovation.com/webhook/v1/additional-information',

    // External API (for form pre-fill)
    externalApi: {
      baseUrl: process.env.EXTERNAL_API_URL || 'https://dcapi.maceinnovation.com',
      endpoints: {
        leadByRefId: '/api/leads/byexternaltransactionid/'
      },
      headers: {
        'x-api-auth': process.env.EXTERNAL_API_AUTH_KEY || 'mK9x$Fp2#vL7nQ4wR8jT6bY3hA5cE1dZ'
      },
      timeout: 10000 // 10 seconds
    },

    // Session Management
    session: {
      timeout: 30 * 60 * 1000,      // 30 minutes
      renewThreshold: 5 * 60 * 1000, // 5 minutes before expiry
      maxRetries: 3,
      storageKey: 'prodebtco_session'
    },
    
    // Lead Detection Thresholds
    leadDetection: {
      partialLeadThreshold: 0.3,     // 30% completion
      completeLeadThreshold: 0.8,    // 80% completion  
      minFieldsForPartialLead: 2,    // Email + one other field
      contactFieldWeight: 0.4,       // 40% weight for contact info
      importantFields: ['email', 'first_name', 'last_name', 'phone', 'full_name']
    },
    
    // Event Processing
    eventProcessing: {
      batchSize: 50,                 // Events per batch
      flushInterval: 5000,           // 5 seconds
      maxQueueSize: 1000,            // Max queued events
      enableRealtime: !isDevelopment // Disable in dev for performance
    },
    
    // Rate Limiting
    rateLimiting: {
      sessionCreation: 10,           // Per minute
      formInteraction: 120,          // Per minute  
      leadProcessing: 5,             // Per minute
      burstLimit: {
        sessionCreation: 2,          // Per second
        formInteraction: 10,         // Per second
        leadProcessing: 1            // Per second
      }
    }
  }
}

// Configuration validation
export const validateConfig = (config: UnifiedConfig): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []
  
  // Validate required Supabase configuration
  if (!config.supabase.url || !config.supabase.url.startsWith('https://')) {
    errors.push('SUPABASE_URL is required and must be a valid HTTPS URL')
  }
  
  if (!config.supabase.anonKey || config.supabase.anonKey.length < 10) {
    errors.push('SUPABASE_ANON_KEY is required and must be a valid key')
  }
  
  // Validate edge function URLs
  for (const [name, url] of Object.entries(config.edgeFunctions.endpoints)) {
    if (!url || !url.startsWith('https://')) {
      errors.push(`Edge function ${name} URL is invalid: ${url}`)
    }
  }
  
  // Validate numeric configurations
  if (config.session.timeout <= 0) {
    errors.push('Session timeout must be positive')
  }
  
  if (config.leadDetection.minFieldsForPartialLead < 1) {
    errors.push('Minimum fields for partial lead must be at least 1')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

// Request headers for API calls
export const getApiHeaders = (config: UnifiedConfig, includeAuth: boolean = true): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
  
  if (includeAuth) {
    headers['apikey'] = config.supabase.anonKey
    headers['Authorization'] = `Bearer ${config.supabase.anonKey}`
  }
  
  return headers
}

// CORS configuration for edge functions
export const corsConfig = {
  allowedOrigins: [
    'http://localhost:3000',
    'http://localhost:8080',
    'https://shawns-accs-poc.webflow.io',
    'https://webflow.io',
    'https://*.webflow.io',
    'https://prodebtco.com',
    'https://*.prodebtco.com'
  ],
  allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS'],
  allowedHeaders: [
    'Origin',
    'X-Requested-With', 
    'Content-Type',
    'Accept',
    'Authorization',
    'apikey',
    'x-client-info'
  ],
  maxAge: 86400 // 24 hours
}

// Create and validate configuration
const unifiedConfig = createUnifiedConfig()
const configValidation = validateConfig(unifiedConfig)

if (!configValidation.isValid) {
  console.warn('⚠️ Unified configuration validation failed:', configValidation.errors)
  
  // In development, show errors clearly
  if (unifiedConfig.debug) {
    console.group('🔧 Configuration Validation Errors')
    configValidation.errors.forEach(error => console.error('❌', error))
    console.groupEnd()
  }
}

// Log configuration loaded successfully
if (unifiedConfig.debug) {
  console.log('🔧 Unified configuration loaded:', {
    environment: unifiedConfig.environment,
    supabaseUrl: unifiedConfig.supabase.url,
    version: unifiedConfig.version,
    sessionTimeout: `${unifiedConfig.session.timeout / 1000 / 60}min`,
    leadThreshold: `${unifiedConfig.leadDetection.partialLeadThreshold * 100}%`
  })
}

// Export the unified configuration and utilities
export const config = unifiedConfig
export const defaultHeaders = getApiHeaders(unifiedConfig)
export default config

// UnifiedConfig interface is already exported above